import { RightOutlined } from '@ant-design/icons';
import { motion, useInView, useReducedMotion } from 'motion/react';
import { Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/locales';
import hero from '@/assets/imgs/shop/hero.png';
import aboutService1 from '@/assets/imgs/shop/about-service-1.png';
import aboutService2 from '@/assets/imgs/shop/about-service-2.png';
import aboutService3 from '@/assets/imgs/shop/about-service-3.png';
import aboutService4 from '@/assets/imgs/shop/about-service-4.png';
import aboutService5 from '@/assets/imgs/shop/about-service-5.png';
import aboutCenter from '@/assets/imgs/shop/about-center.png';
import aboutOuterArc from '@/assets/imgs/shop/about-outer-arc.png';
import aboutInnerArc from '@/assets/imgs/shop/about-inner-arc.svg';
import aboutArcPoint from '@/assets/imgs/shop/about-arc-point.svg';
import touchpointVisible from '@/assets/imgs/shop/touchpoint-visible.png';
import touchpointStart from '@/assets/imgs/shop/touchpoint-start.png';
import touchpointInfo from '@/assets/imgs/shop/touchpoint-info.png';
import combinationTourist from '@/assets/imgs/shop/combination-tourist.png';
import combinationCheck from '@/assets/imgs/shop/combination-check.svg';
import combinationVisitArrow from '@/assets/imgs/shop/combination-visit-arrow.svg';
import combinationSelectArrow from '@/assets/imgs/shop/combination-select-arrow.svg';
import combinationProviderArrowTop from '@/assets/imgs/shop/combination-provider-arrow-top.svg';
import combinationProviderArrowMiddle from '@/assets/imgs/shop/combination-provider-arrow-middle.svg';
import combinationProviderArrowBottom from '@/assets/imgs/shop/combination-provider-arrow-bottom.svg';
import combinationMerchantIcon from '@/assets/imgs/shop/combination-merchant-icon.png';
import combinationProviderIcon from '@/assets/imgs/shop/combination-provider-icon.png';
import bilionpassLogo from '@/assets/imgs/header/logo.png';
import featureLowCost from '@/assets/imgs/shop/feature-low-cost.png';
import featureManage from '@/assets/imgs/shop/feature-manage.png';
import featureChannel from '@/assets/imgs/shop/feature-channel.png';
import featureFeedback from '@/assets/imgs/shop/feature-feedback.png';
import exampleRetail from '@/assets/imgs/shop/example-retail.png';
import exampleFood1 from '@/assets/imgs/shop/example-food-layer-1.png';
import exampleFood2 from '@/assets/imgs/shop/example-food-layer-2.png';
import exampleFood3 from '@/assets/imgs/shop/example-food-layer-3.png';
import exampleTour1 from '@/assets/imgs/shop/example-tour-layer-1.png';
import exampleTour2 from '@/assets/imgs/shop/example-tour-layer-2.png';
import stepRegister from '@/assets/imgs/shop/step-register.png';
import stepStore from '@/assets/imgs/shop/step-store.png';
import stepStart from '@/assets/imgs/shop/step-start.png';
import stepArrow from '@/assets/imgs/shop/step-arrow.svg';
import processLabelPointer from '@/assets/imgs/shop/process-label-pointer.svg';
import servicesCtaBackground from '@/assets/imgs/shop/services-cta-bg.png';

const Canvas = ({ children }: { children: React.ReactNode }) => (
  <div className="relative left-1/2 h-full w-[1920px] -translate-x-1/2">{children}</div>
);

const PrimaryButton = ({
  children,
  href,
  inverse = false,
  showComingSoon = true,
}: {
  children: React.ReactNode;
  href: string;
  inverse?: boolean;
  showComingSoon?: boolean;
}) => (
  <span className="relative inline-flex">
    <a
      href={href ? href : undefined}
      className={`group inline-flex h-[80px] min-w-[320px] items-center justify-center overflow-hidden rounded-full border-2 border-[#00a0e9] px-[40px] text-[37px] font-medium transition-[background-color,color,border-color] duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:duration-0 ${
        inverse
          ? 'bg-white text-[#00a0e9] hover:border-white hover:bg-[#00a0e9] hover:text-white'
          : 'bg-[#00a0e9] text-white hover:bg-white hover:text-[#00a0e9]'
      }`}
    >
      <span className="flex items-center gap-[13px] whitespace-nowrap transition-transform duration-[360ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-[20px] motion-reduce:duration-0">
        {children}
        <RightOutlined
          aria-hidden="true"
          className="text-[26px] opacity-100 transition-opacity duration-[120ms] group-hover:opacity-0 motion-reduce:duration-0"
        />
      </span>
    </a>
    {showComingSoon && (
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-[100px] -top-[22px] z-10 flex h-[43px] items-center rounded-[7px] bg-[#ff5d21] px-[10px] text-[23px] font-medium leading-none text-white after:absolute after:left-[7px] after:top-full after:border-r-[9px] after:border-t-[9px] after:border-r-transparent after:border-t-[#ff5d21] after:content-['']"
      >
        Coming Soon
      </span>
    )}
  </span>
);

const aboutImages = [
  {
    src: aboutService1,
    className: 'left-[368px] top-[1003px] h-[207px] w-[241px]',
    initial: { opacity: 0, scaleX: 0.3, scaleY: 0.3, x: 0 },
    animate: {
      opacity: [0, 1, 1, 0],
      scaleX: [0.3, 1, 1, 0.3],
      scaleY: [0.3, 1, 1, 0.3],
      x: [0, 0, 462],
    },
    times: [0, 0.0772, 0.9615, 1],
    scaleEnters: true,
    xHolds: true,
    rest: { x: 0, y: 0 },
  },
  {
    src: aboutService2,
    className: 'left-[516px] top-[684px] h-[232px] w-[200px]',
    initial: { opacity: 0, scaleX: 0.3, scaleY: 0.3, x: -150, y: 300.8 },
    animate: {
      opacity: [0, 1, 1, 0],
      scaleX: [0.3, 1, 1, 0.3],
      scaleY: [0.3, 1, 1, 0.3],
      x: [-150, 3, 3, 341],
      y: [300.8, 13, 13, 306.8],
    },
    times: [0, 0.0963, 0.9615, 1],
    scaleEnters: true,
    xHolds: false,
    rest: { x: 3, y: 13 },
  },
  {
    src: aboutService3,
    className: 'left-[857px] top-[573px] size-[212px]',
    initial: { opacity: 0, scaleX: 1, scaleY: 1, x: -497, y: 422 },
    animate: {
      opacity: [0, 1, 1, 0],
      scaleX: [1, 1, 0.3],
      scaleY: [1, 1, 0.3],
      x: [-497, 0, 0, -8.2],
      y: [422, 0, 0, 415.801],
    },
    times: [0, 0.1154, 0.9615, 1],
    scaleEnters: false,
    xHolds: false,
    rest: { x: 0, y: 0 },
  },
  {
    src: aboutService4,
    className: 'left-[1161px] top-[670px] size-[244px]',
    initial: { opacity: 0, scaleX: 1, scaleY: 1, x: -817, y: 309 },
    animate: {
      opacity: [0, 1, 1, 0],
      scaleX: [1, 1, 0.3],
      scaleY: [1, 1, 0.3],
      x: [-817, -8, -8, -332.4],
      y: [309, 3, 3, 302.6],
    },
    times: [0, 0.1342, 0.9615, 1],
    scaleEnters: false,
    xHolds: false,
    rest: { x: -8, y: 3 },
  },
  {
    src: aboutService5,
    className: 'left-[1332px] top-[1021px] h-[164px] w-[237px]',
    initial: { opacity: 0, scaleX: 1, scaleY: 1, x: -985, y: -2 },
    animate: {
      opacity: [0, 1, 1, 0],
      scaleX: [1, 1, 0.3],
      scaleY: [1, 1, 0.3],
      x: [-985, 3.5, 3.5, -490.95],
      y: [-2, -1, -1, -8.4],
    },
    times: [0, 0.1543, 0.9615, 1],
    scaleEnters: false,
    xHolds: false,
    rest: { x: 3.5, y: -1 },
  },
];

const aboutLabels = [
  {
    titleKey: 'serviceImport',
    className: 'left-[260px] top-[890px] rounded-bl-[50px] rounded-tl-[50px] rounded-tr-[50px]',
    englishClassName: 'left-[180px] top-[890px] rounded-bl-[50px] rounded-tl-[50px] rounded-tr-[50px]',
    origin: '100% 100%',
    appear: 0.065,
    settle: 0.12,
    fadeEnd: 0.986,
    endX: 493,
    endY: 130.8,
  },
  {
    titleKey: 'serviceApplication',
    className: 'left-[408px] top-[586px] rounded-bl-[50px] rounded-tl-[50px] rounded-tr-[50px]',
    origin: '100% 100%',
    appear: 0.0893,
    settle: 0.1443,
    fadeEnd: 0.9863,
    endX: 333,
    endY: 430.8,
  },
  {
    titleKey: 'serviceSettlement',
    className: 'left-[852px] top-[450px] rounded-[50px]',
    origin: '50% 100%',
    appear: 0.1092,
    settle: 0.1642,
    fadeEnd: 0.9863,
    endX: -8,
    endY: 567.8,
  },
  {
    titleKey: 'contractManagement',
    className: 'left-[1297px] top-[586px] rounded-br-[50px] rounded-tl-[50px] rounded-tr-[50px]',
    origin: '0% 100%',
    appear: 0.1277,
    settle: 0.1827,
    fadeEnd: 0.9863,
    endX: -342,
    endY: 428.2,
  },
  {
    titleKey: 'usageSearch',
    className: 'left-[1445px] top-[890px] rounded-br-[50px] rounded-tl-[50px] rounded-tr-[50px]',
    origin: '0% 100%',
    appear: 0.1455,
    settle: 0.2005,
    fadeEnd: 0.9865,
    endX: -490,
    endY: 128.2,
  },
];

const aboutArcPoints = [
  'left-[637px] top-[1095px]',
  'left-[1271px] top-[1095px]',
  'left-[744px] top-[928px]',
  'left-[1165px] top-[928px]',
  'left-[951px] top-[851px]',
] as const;

function AboutTimeline() {
  const { t } = useTranslation();
  const labelPadding = i18n.resolvedLanguage === 'en-US' ? 'px-[32px]' : 'px-[48px]';
  return (
    <>
      {aboutImages.map(item => (
        <div key={item.src} className={`absolute ${item.className}`}>
          <motion.img
            src={item.src}
            alt=""
            className="object-contain size-full will-change-transform"
            initial={item.initial}
            animate={item.animate}
            transition={{
              opacity: {
                duration: 10.4,
                times: item.times,
                ease: [[0.5, 0, 0.5, 1], 'linear', [0.5, 0, 0.5, 1]],
                repeat: Infinity,
              },
              scaleX: {
                duration: 10.4,
                times: item.scaleEnters ? item.times : [0, 0.9615, 1],
                ease: item.scaleEnters
                  ? ['easeInOut', 'linear', [0.5, 0, 0.5, 1]]
                  : ['linear', [0.5, 0, 0.5, 1]],
                repeat: Infinity,
              },
              scaleY: {
                duration: 10.4,
                times: item.scaleEnters ? item.times : [0, 0.9615, 1],
                ease: item.scaleEnters
                  ? ['easeInOut', 'linear', [0.5, 0, 0.5, 1]]
                  : ['linear', [0.5, 0, 0.5, 1]],
                repeat: Infinity,
              },
              x: {
                duration: 10.4,
                times: item.xHolds ? [0, 0.9615, 1] : item.times,
                ease: item.xHolds ? 'linear' : ['easeInOut', 'linear', 'linear'],
                repeat: Infinity,
              },
              ...('y' in item.animate
                ? {
                    y: {
                      duration: 10.4,
                      times: item.times,
                      ease: ['easeInOut', 'linear', 'linear'],
                      repeat: Infinity,
                    },
                  }
                : {}),
            }}
          />
        </div>
      ))}
      {aboutLabels.map(item => (
        <div
          key={item.className}
          className={`absolute h-[84px] ${i18n.resolvedLanguage === 'en-US' && item.englishClassName ? item.englishClassName : item.className}`}
        >
          <motion.div
            className={`${labelPadding} flex size-full items-center justify-center whitespace-pre-line bg-[#17b4ff] text-center text-[28px] font-semibold leading-[1.1] text-white [border-radius:inherit] will-change-transform`}
            style={{ transformOrigin: item.origin }}
            initial={{ opacity: 0, scaleX: 0.6, scaleY: 0.6, x: 0, y: 0 }}
            animate={{
              opacity: [0, 0, 1, 1, 0, 0],
              scaleX: [0.6, 0.6, 1, 1, 0.3],
              scaleY: [0.6, 0.6, 1, 1, 0.3],
              x: [0, 0, item.endX],
              y: [0, 0, item.endY],
            }}
            transition={{
              opacity: {
                duration: 10.4,
                times: [0, item.appear, item.settle, 0.9615, item.fadeEnd, 1],
                ease: ['linear', [0.5, 0, 0.5, 1], 'linear', [0.5, 0, 0.5, 1], 'linear'],
                repeat: Infinity,
              },
              scaleX: {
                duration: 10.4,
                times: [0, item.appear, item.settle, 0.9615, 1],
                ease: ['linear', [0.45, 1.45, 0.8, 1], 'linear', [0.5, 0, 0.5, 1]],
                repeat: Infinity,
              },
              scaleY: {
                duration: 10.4,
                times: [0, item.appear, item.settle, 0.9615, 1],
                ease: ['linear', [0.45, 1.45, 0.8, 1], 'linear', [0.5, 0, 0.5, 1]],
                repeat: Infinity,
              },
              x: {
                duration: 10.4,
                times: [0, 0.9615, 1],
                ease: ['linear', 'easeIn'],
                repeat: Infinity,
              },
              y: {
                duration: 10.4,
                times: [0, 0.9615, 1],
                ease: ['linear', 'easeIn'],
                repeat: Infinity,
              },
            }}
          >
            {t(`shop.about.timeline.${item.titleKey}`)}
          </motion.div>
        </div>
      ))}
    </>
  );
}

function AboutMotion() {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { amount: 0.25 });
  const reduceMotion = useReducedMotion();
  const labelPadding = i18n.resolvedLanguage === 'en-US' ? 'px-[32px]' : 'px-[48px]';

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none">
      {reduceMotion ? (
        <>
          {aboutImages.map(item => (
            <img
              key={item.src}
              src={item.src}
              alt=""
              className={`absolute object-contain ${item.className}`}
              style={{ transform: `translate(${item.rest.x}px, ${item.rest.y}px)` }}
            />
          ))}
          {aboutLabels.map(item => (
            <div
              key={item.className}
              className={`absolute flex h-[84px] items-center justify-center whitespace-pre-line bg-[#17b4ff] ${labelPadding} text-center text-[28px] font-semibold leading-[1.1] text-white ${i18n.resolvedLanguage === 'en-US' && item.englishClassName ? item.englishClassName : item.className}`}
            >
              {t(`shop.about.timeline.${item.titleKey}`)}
            </div>
          ))}
        </>
      ) : isInView ? (
        <AboutTimeline />
      ) : null}
    </div>
  );
}

const touchpointCards = [
  {
    image: touchpointVisible,
    titleKey: 'visibility',
    pointKeys: [
      'storeInfo',
      'productInfo',
      'digitalCoupons',
      'overseasChannels',
      'multilingualInfo',
    ],
  },
  {
    image: touchpointStart,
    titleKey: 'startService',
    pointKeys: ['cloudService', 'flexibleFeatures', 'storeManagement', 'easyUpdates', 'multiStore'],
  },
  {
    image: touchpointInfo,
    titleKey: 'clearInfo',
    pointKeys: [
      'hoursAndAccess',
      'productAndService',
      'paymentMethods',
      'taxFree',
      'coupons',
      'reservationPickup',
    ],
  },
] as const;

const features = [
  { image: featureChannel, key: 'channel' },
  { image: featureFeedback, key: 'feedback' },
  { image: featureManage, key: 'manage' },
  { image: featureLowCost, key: 'lowCost' },
] as const;

const examples = [
  {
    key: 'retail',
    images: [exampleRetail],
    tagKeys: ['productInfo', 'taxFreeService', 'deliverySupport'],
  },
  {
    key: 'food',
    images: [exampleFood3],
    tagKeys: ['facilityInfo', 'multilingualGuide', 'couponDistribution'],
  },
  {
    key: 'tourism',
    images: [exampleTour2],
    tagKeys: ['facilityInfo', 'couponDistribution', 'multilingualGuide'],
  },
] as const;

const steps = [
  { image: stepRegister, key: 'register' },
  { image: stepStore, key: 'store' },
  { image: stepStart, key: 'start' },
] as const;

const merchantServiceKeys = ['coupon', 'delivery', 'taxRefund'] as const;
const platformServiceKeys = [
  'coupon',
  'delivery',
  'taxRefund',
  'storeReservation',
  'taxFreeReservation',
  'payment',
] as const;
const checkedPlatformServiceKeys = new Set<(typeof platformServiceKeys)[number]>([
  'coupon',
  'delivery',
  'taxRefund',
]);
const providers = [
  { titleKey: 'travelPlatform', serviceKey: 'coupon' },
  { titleKey: 'logisticsCompany', serviceKey: 'delivery' },
  { titleKey: 'taxFreeProvider', serviceKey: 'taxRefund' },
] as const;

const getCombinationLabelFontSize = (language: string) =>
  language.startsWith('en')
    ? 'text-[22px]'
    : language.startsWith('ja')
      ? 'text-[26px]'
      : 'text-[30px]';

function ServicePill({
  label,
  checked = false,
  width,
}: {
  label: string;
  checked?: boolean;
  width: 184 | 221 | 250;
}) {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const widthClass = width === 184 ? 'w-[184px]' : width === 221 ? 'w-[221px]' : 'w-[250px]';
  const labelFontSizeClass = getCombinationLabelFontSize(language);

  return (
    <div
      className={`relative flex h-[50px] items-center justify-center rounded-[15px] border-2 border-[#00a0e9] bg-white px-[12px] text-center leading-none text-[#00a0e9] ${widthClass}`}
    >
      <span className={`max-w-full whitespace-nowrap ${labelFontSizeClass}`}>
        {label}
      </span>
      {checked && (
        <img
          src={combinationCheck}
          alt=""
          className={`absolute top-[14px] size-[40px] -right-[6px]`}
        />
      )}
    </div>
  );
}

function ServiceCombinationSection() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const labelFontSizeClass = getCombinationLabelFontSize(language);

  return (
    <section className="relative h-[1065px] overflow-hidden rounded-t-[64px] bg-white">
      <div className="mx-auto w-[1400px] pt-[120px]">
        <h2 className="text-center font-douyin text-[50px] font-bold leading-none">
          {t('shop.combination.title')}
        </h2>

        <div className="mt-[57px] flex h-[667px] items-start">
          <div className="mt-[87px] flex h-[580px] w-[172px] shrink-0 flex-col items-center">
            <h3 className="mt-[23px] text-center text-[40px] font-semibold leading-none">
              {t('shop.combination.visitor')}
            </h3>
            <img
              src={combinationTourist}
              alt=""
              className="mt-[34px] h-[360px] w-[170px] object-contain"
            />
          </div>

          <div className="relative z-20 mt-[290px] -ml-[27px] flex w-[135px] shrink-0 flex-col items-center text-center">
            <img src={combinationVisitArrow} alt="" className="h-[83px] w-[135px]" />
            <p className={`mt-[18px] whitespace-nowrap leading-none text-[#00a0e9] ${labelFontSizeClass}`}>
              {t('shop.combination.visitStore')}
            </p>
          </div>

          <div className="relative z-10 mt-[161px] -ml-[49px] flex size-[368px] shrink-0 flex-col items-center rounded-full bg-[#eef8ff]">
            <div className="mt-[27px] flex h-[100px] items-center gap-[10px]">
              <img
                src={combinationMerchantIcon}
                alt=""
                className="h-[100px] w-[90px] object-contain"
              />
              <h3 className="text-[40px] font-semibold leading-none">
                {t('shop.combination.merchant')}
              </h3>
            </div>
            <div className="mt-[20px] flex flex-col gap-[18px]">
              {merchantServiceKeys.map(key => (
                <ServicePill
                  key={key}
                  label={t(`shop.combination.services.${key}`)}
                  checked
                  width={184}
                />
              ))}
            </div>
          </div>

          <div className="relative z-20 mt-[290px] -ml-[54px] flex w-[135px] shrink-0 flex-col items-center text-center">
            <img src={combinationSelectArrow} alt="" className="h-[83px] w-[135px]" />
            <p className={`mt-[18px] whitespace-nowrap leading-none text-[#00a0e9] ${labelFontSizeClass}`}>
              {t('shop.combination.selectFreely')}
            </p>
          </div>

          <div className="relative z-10 mt-[82px] -ml-[66px] flex size-[526px] shrink-0 flex-col items-center rounded-full bg-[#eef8ff] pt-[34px]">
            <div className="w-[213px] text-center">
              <img
                src={bilionpassLogo}
                alt="BilionPass"
                className="h-[48px] w-[213px] object-contain"
              />
              <p className="mt-[4px] tracking-[9px] text-[12px] font-semibold leading-none text-[#00a0e9]">
                SHOPS
              </p>
            </div>
            <div className="mt-[17px] flex flex-col gap-[8px]">
              {platformServiceKeys.map(key => (
                <ServicePill
                  key={key}
                  label={t(`shop.combination.services.${key}`)}
                  checked={checkedPlatformServiceKeys.has(key)}
                  width={250}
                />
              ))}
            </div>
          </div>

          <div className="relative z-20 mt-[76px] -ml-[120px] grid h-[537px] w-[142px] shrink-0 grid-rows-3 items-center justify-items-start">
            <img
              src={combinationProviderArrowTop}
              alt=""
              className="h-[83px] w-[163px] max-w-none -rotate-[30deg]"
            />
            <img
              src={combinationProviderArrowMiddle}
              alt=""
              className="h-[83px] w-[163px] max-w-none"
            />
            <img
              src={combinationProviderArrowBottom}
              alt=""
              className="h-[83px] w-[163px] max-w-none rotate-[30deg]"
            />
          </div>

          <div className="relative z-10 ml-[18px] flex w-[220px] shrink-0 flex-col items-center gap-[15px]">
            {providers.map(provider => {
              const providerTitle = t(`shop.combination.providers.${provider.titleKey}`);
              return (
                <div
                  key={provider.titleKey}
                  className="flex size-[220px] flex-col items-center rounded-full bg-[#eef8ff] pt-[61px]"
                >
                  <div className="flex h-[52px] items-center gap-[5px] whitespace-nowrap">
                    <img
                      src={combinationProviderIcon}
                      alt=""
                      className="h-[60px] w-[58px] object-contain"
                    />
                    <h3
                      className={`font-semibold leading-none ${
                        providerTitle.length > 8 ? 'text-[25px]' : 'text-[32px]'
                      }`}
                    >
                      {providerTitle}
                    </h3>
                  </div>
                  <div className="mt-[25px]">
                    <ServicePill
                      label={t(`shop.combination.services.${provider.serviceKey}`)}
                      width={221}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

const Shop = () => {
  const { t, i18n } = useTranslation();
  const touchpointTitleSize = i18n.resolvedLanguage === 'en-US' ? 'text-[30px]' : 'text-[35px]';
  const touchpointPointSize = i18n.resolvedLanguage === 'en-US' ? 'text-[22px]' : 'text-[24px]';
  const exampleHeadlineSize = i18n.resolvedLanguage === 'zh-CN' ? 'text-[30px]' : 'text-[28px]';

  return (
    <div className="min-w-[1480px] overflow-hidden bg-white text-[#355f72]">
      <section className="h-[500px] bg-white mb-[50px]">
        <div className="mx-auto mt-[50px] grid h-[500px] w-[1800px] grid-cols-1 grid-rows-1 overflow-hidden rounded-[30px]">
          <img
            src={hero}
            alt="BilionPass Shops"
            className="block object-cover col-start-1 row-start-1 size-full"
          />
          <div className="flex flex-col items-center justify-center w-full h-full col-start-1 row-start-1 text-center text-white">
            <h1 className="font-douyin text-[70px] font-bold leading-[1.5]">BilionPass Shops</h1>
            <p className="mt-[2px] text-[50px] font-medium leading-[1.5]">
              {t('shop.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      <section className="relative h-[1600px] bg-white">
        <Canvas>
          <h2 className="absolute inset-x-0 top-[32px] text-center font-douyin text-[70px] font-bold leading-[1.5] text-[#00a0e9]">
            {t('shop.about.title')}
          </h2>
          <p className="absolute inset-x-0 top-[143px] whitespace-pre-line text-center text-[40px] font-semibold leading-[1.4]">
            {t('shop.about.lead')}
          </p>
          <p className="absolute inset-x-0 top-[284px] whitespace-pre-line text-center text-[30px] leading-[1.5]">
            {t('shop.about.description')}
          </p>

          <img
            src={aboutOuterArc}
            alt=""
            className="absolute left-[311px] top-[521px] h-[649px] w-[1297px] object-contain"
          />
          <div className="absolute left-[633px] top-[858px] z-20 h-[327.35px] w-[654.7px]">
            <img
              src={aboutInnerArc}
              alt=""
              className="absolute -left-[2.5px] -top-[2.5px] h-[332.35px] w-[659.7px] max-w-none"
            />
          </div>
          <img
            src={aboutCenter}
            alt={t('shop.images.serviceManagement')}
            className="absolute left-[733px] top-[925px] z-10 w-[454px] object-contain"
          />
          {aboutArcPoints.map(position => (
            <img
              key={position}
              src={aboutArcPoint}
              alt=""
              className={`absolute size-[12px] ${position}`}
            />
          ))}
          <AboutMotion />

          <div className="absolute left-1/2 top-[1211px] h-[81px] w-[1094px] -translate-x-1/2 bg-gradient-to-r from-transparent via-[#17b4ff]/20 to-transparent" />
          <p className="absolute inset-x-0 top-[1237px] text-center text-[40px] font-semibold leading-none text-[#17b4ff]">
            {t('shop.about.support')}
          </p>
          <div className="absolute inset-x-0 top-[1364px] text-center">
            <PrimaryButton href="/contact" showComingSoon={false}>
              {t('shop.about.requestMaterials')}
            </PrimaryButton>
          </div>
          <p className="absolute inset-x-0 top-[1480px] text-center text-[28px] leading-none text-black">
            {t('shop.guide')}
          </p>
        </Canvas>
      </section>

      <section
        id="shop-services"
        className="h-[1080px] overflow-hidden rounded-t-[64px] bg-[#dff1ff]"
      >
        <h2 className="pt-[109px] text-center font-douyin text-[50px] font-bold leading-none">
          {t('shop.touchpoints.title')}
        </h2>
        <div className="mx-auto mt-[74px] grid w-fit grid-cols-3 gap-[25px]">
          {touchpointCards.map(card => (
            <article
              key={card.image}
              className="h-[729px] w-[450px] rounded-[40px] bg-white px-[34px] pt-[18px]"
            >
              <img src={card.image} alt="" className="mx-auto h-[320px] w-[390px] object-contain" />
              <h3 className={`mt-[5px] whitespace-pre-line ${touchpointTitleSize} font-semibold leading-[1.3]`}>
                {t(`shop.touchpoints.cards.${card.titleKey}.title`)}
              </h3>
              {i18n.resolvedLanguage === 'zh-CN' ? (
                <p className={`mt-[20px] ${touchpointPointSize} leading-[1.7]`}>
                  {t(`shop.touchpoints.cards.${card.titleKey}.description`)}
                </p>
              ) : (
                <ul className={`mt-[20px] list-disc pl-[30px] ${touchpointPointSize} leading-[1.7]`}>
                  {card.pointKeys.map(pointKey => (
                    <li key={pointKey}>
                      {t(`shop.touchpoints.cards.${card.titleKey}.points.${pointKey}`)}
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </section>

      <ServiceCombinationSection />

      <section className="h-[1238px] overflow-hidden rounded-t-[64px] bg-[#dff1ff]">
        <h2 className="pt-[87px] text-center font-douyin text-[50px] font-bold leading-none">
          {t('shop.features.title')}
        </h2>
        <div className="mx-auto mt-[72px] grid w-fit grid-cols-2 gap-[40px]">
          {features.map(feature => (
            <article
              key={feature.key}
              className="flex h-[436px] w-[680px] flex-col items-center border border-[#00a0e9] bg-white px-[30px] text-center"
            >
              <img
                src={feature.image}
                alt=""
                className="mt-[10px] h-[255px] w-[500px] object-contain"
              />
              <h3 className="text-[35px] font-medium leading-[1.5] whitespace-nowrap">
                {t(`shop.features.items.${feature.key}.title`)}
              </h3>
              <p className="mt-[3px] whitespace-pre-line text-[25px] leading-[1.4]">
                {t(`shop.features.items.${feature.key}.description`)}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="relative h-[1069px] bg-white">
        <Canvas>
          <h2 className="absolute inset-x-0 top-[116px] text-center font-douyin text-[50px] font-bold leading-none">
            {t('shop.examples.title')}
          </h2>
          <div className="absolute left-[259px] top-[239px] flex gap-[55px]">
            {examples.map(example => (
              <article key={example.key} className="w-[430px]">
                <div className="relative h-[260px] overflow-hidden rounded-[15px]">
                  {example.images.map((image, index) => (
                    <img
                      key={image}
                      src={image}
                      alt=""
                      className="absolute inset-0 object-cover size-full"
                      style={{ zIndex: index }}
                    />
                  ))}
                  <h3 className="absolute bottom-0 left-0 z-10 h-[50px] rounded-bl-[15px] bg-[#355f72] px-[20px] text-[30px] font-semibold leading-[50px] text-white">
                    {t(`shop.examples.items.${example.key}.title`)}
                  </h3>
                </div>
                <div className="mt-[20px] flex gap-[9px]">
                  {example.tagKeys.map(tagKey => (
                    <span
                      key={tagKey}
                      className="rounded-[8px] bg-[#00a0e9] px-[11px] py-[5px] text-[17px] leading-[30px] text-white"
                    >
                      {t(`shop.examples.tags.${tagKey}`)}
                    </span>
                  ))}
                </div>
                <h4 className={`mt-[20px] ${exampleHeadlineSize} font-semibold leading-[1.5]`}>
                  {t(`shop.examples.items.${example.key}.headline`)}
                </h4>
                <p className="mt-[20px] text-[24px] leading-[1.7] text-[#565656]">
                  {t(`shop.examples.items.${example.key}.description`)}
                </p>
              </article>
            ))}
          </div>
        </Canvas>
      </section>

      <section className="h-[1100px] overflow-hidden rounded-t-[64px] bg-[#dff1ff]">
        <div className="mx-auto w-[1400px] pt-[83px]">
          <div className="ml-[800px] flex w-[140px] flex-col items-center">
            <div className="flex h-[32px] w-[140px] items-center justify-center rounded-[5px] bg-[#00a0e9] text-center text-[19.44px] font-semibold leading-none text-white">
              {t('shop.process.online')}
            </div>
            <img src={processLabelPointer} alt="" className="ml-[5px] mt-[-3px] size-[9px]" />
          </div>
          <h2 className="mt-[-8px] text-center font-douyin text-[50px] font-bold leading-none">
            {t('shop.process.title')}
          </h2>
          <div className="mt-[94px] grid min-h-[543px] grid-cols-[400px_100px_400px_100px_400px]">
            {steps.map((step, index) => (
              <Fragment key={step.key}>
                <div className="w-[400px] text-center">
                  <div className="grid size-[400px] grid-cols-1 grid-rows-1 border border-[#00a0e9] bg-white">
                    <img
                      src={step.image}
                      alt=""
                      className="col-start-1 row-start-1 block size-full object-contain p-[15px]"
                    />
                    <span className="col-start-1 row-start-1 h-[50px] w-[128px] self-start justify-self-start bg-[#355f72] text-[30px] font-semibold leading-[50px] text-white">
                      Step {index + 1}
                    </span>
                  </div>
                  <h3 className="mt-[10px] text-[35px] font-semibold leading-[1.5]">
                    {t(`shop.process.steps.${step.key}.title`)}
                  </h3>
                  <p className="mt-[10px] whitespace-pre-line text-[25px] leading-[1.4]">
                    {t(`shop.process.steps.${step.key}.description`)}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <img
                    src={stepArrow}
                    alt={t('shop.process.nextStep')}
                    className="mt-[165px] size-[70px] justify-self-center"
                  />
                )}
              </Fragment>
            ))}
          </div>
          <div className="mt-[72px] text-center">
            <PrimaryButton href="">{t('shop.process.register')}</PrimaryButton>
          </div>
        </div>
      </section>

      <section
        className="h-[500px] bg-[#00a0e9] bg-cover bg-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 160, 233, 0.35), rgba(0, 160, 233, 0.35)), url(${servicesCtaBackground})`,
        }}
      >
        <div className="mx-auto flex h-full w-[1400px] items-start pt-[115px]">
          <div className="w-[530px] mr-[50px] font-expose text-[86px] font-black uppercase leading-[0.99]">
            BilionPass
            <br />
            Shops
          </div>
          <div className="pt-[6px] flex flex-col flex-1 ">
            <h2 className="font-douyin text-[45px] font-bold leading-[1.5]">
              {t('shop.cta.title')}
            </h2>
            <div className="mt-[18px]">
              <PrimaryButton href="" inverse>
                {t('shop.cta.button')}
              </PrimaryButton>
            </div>
            <p className="mt-[54px] text-[25px] leading-none">{t('shop.guide')}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Shop;
